import { formatAccidentals, splitChordId } from "./theory-helpers";

/**
 * How note names are spelled on screen.
 *
 * - `english`: C, D, E … the international standard, and the spelling every
 *   chord chart, tutorial and lead sheet out there uses.
 * - `latin`: Do, Ré, Mi … French solfège, the names a French adult beginner
 *   already knows from school and nursery rhymes.
 *
 * This is a **display** concern only. The domain (chord ids, catalog entries,
 * VEF files, routes, comparisons) always speaks `english` — see the warning
 * on `formatAccidentals`, the same rule applies here.
 */
export type NoteNotation = "english" | "latin";

/** French UI ⇒ solfège reads as the natural default for note names. */
export const DEFAULT_NOTE_NOTATION: NoteNotation = "latin";

/**
 * Chord symbols stay international by default even when note names are in
 * solfège: `Cmaj7` is what the user will find on every chart and tutorial,
 * and translating it cuts them off from that whole ecosystem.
 */
export const DEFAULT_CHORD_NOTATION: NoteNotation = "english";

/**
 * The notation the user did *not* pick.
 *
 * For the few surfaces that deliberately show both spellings side by side —
 * the scale frieze teaches the bridge between them — so that the secondary
 * line follows the preference instead of being hardcoded to solfège.
 */
export function otherNotation(notation: NoteNotation): NoteNotation {
  return notation === "latin" ? "english" : "latin";
}

/** The seven naturals — the only mapping solfège actually needs. Accidentals
 *  and octave numbers ride along untouched. */
const LATIN_NATURALS: Record<string, string> = {
  C: "Do",
  D: "Ré",
  E: "Mi",
  F: "Fa",
  G: "Sol",
  A: "La",
  B: "Si",
};

/** `C`, `F#`, `Bb`, `C4`, `Eb3`, `A-1` … letter + accidentals + octave. */
const NOTE_RE = /^([A-G])([#b]*)(-?\d+)?$/;

/**
 * Separator between a solfège root and its chord suffix. Non-breaking, and
 * load-bearing for legibility: without it `Cm7` renders as `Dom7`, which
 * reads as "Dom 7" rather than "Do m7".
 */
export const ROOT_SUFFIX_GAP = " ";

/**
 * Colle une tonique et son suffixe comme le fait un chiffrage.
 *
 * La règle est celle de `chordSymbolLabel` et elle sort ici pour que les
 * autres surfaces — l'abrégé d'une tonalité, « Dm » ou « Ré m » — l'appliquent
 * au lieu de la réinventer avec une espace en dur. Une lettre seule se colle à
 * son suffixe ; un nom de solfège a besoin de l'espace insécable, sans quoi « Do m »
 * se lirait « Dom ».
 */
export function joinRootSuffix(
  root: string,
  suffix: string,
  notation: NoteNotation,
): string {
  if (!suffix) {
    return root;
  }

  return notation === "latin"
    ? `${root}${ROOT_SUFFIX_GAP}${suffix}`
    : `${root}${suffix}`;
}

const UNICODE_SHARP = "♯";
const UNICODE_FLAT = "♭";

/**
 * Accidentals-only glyph pass. Safe to replace every `b` here because the
 * caller has already isolated the accidental run — unlike `formatAccidentals`,
 * which sees whole labels and must guard against the letter `b` in words.
 */
function accidentalGlyphs(raw: string): string {
  return raw.replace(/#/g, UNICODE_SHARP).replace(/b/g, UNICODE_FLAT);
}

/**
 * Spells a single note name for display: `"F#"` → `"Fa♯"` / `"F♯"`.
 *
 * Accepts an optional octave (`"C4"` → `"Do4"`). Anything unparseable is
 * passed through `formatAccidentals` so an unexpected label still renders
 * with proper glyphs rather than disappearing.
 */
export function noteLabel(name: string, notation: NoteNotation): string {
  const match = NOTE_RE.exec(name.trim());

  if (!match) {
    return formatAccidentals(name);
  }

  const [, letter, accidentals, octave] = match;
  const head = notation === "latin" ? LATIN_NATURALS[letter] : letter;

  return `${head}${accidentalGlyphs(accidentals)}${octave ?? ""}`;
}

/** `noteLabel` over a list, for the note rows of chord cards and grids. */
export function noteLabels(names: string[], notation: NoteNotation): string[] {
  return names.map((n) => noteLabel(n, notation));
}

/** Longest first, so a `Sol` prefix is never mistaken for anything shorter. */
const LATIN_ROOTS = ["Sol", "Do", "Ré", "Mi", "Fa", "La", "Si"];

/**
 * True when the symbol already went through a latin pass.
 *
 * Guards against double translation: `"Do maj7"` re-parsed as english would
 * split into root `D` + suffix `o maj7` and render `"Ré o maj7"`. Display
 * helpers get composed all over the component tree, so the second pass has
 * to be a no-op.
 *
 * Only `Do` and `Fa` are ambiguous (`D`/`F` are real english roots), and the
 * character right after the root settles it: a genuine english symbol like
 * `"Fadd9"` continues with a plain letter, never with a glyph, gap or slash.
 */
function isAlreadyLatin(symbol: string): boolean {
  for (const root of LATIN_ROOTS) {
    if (!symbol.startsWith(root)) {
      continue;
    }

    const rest = symbol.slice(root.length);

    if (rest === "" || /^[♯♭ /]/.test(rest)) {
      return true;
    }
  }

  return false;
}

/**
 * Splits a trailing slash-bass off a chord symbol.
 *
 * Only treats the tail as a bass note when it actually parses as one, which
 * is what keeps `"C6/9"` intact — `9` is not a note, so the `/` belongs to
 * the quality, not to a bass.
 */
function splitSlashBass(symbol: string): { head: string; bass: string | null } {
  const cut = symbol.lastIndexOf("/");

  if (cut <= 0) {
    return { head: symbol, bass: null };
  }

  const tail = symbol.slice(cut + 1);

  if (!NOTE_RE.test(tail)) {
    return { head: symbol, bass: null };
  }

  return { head: symbol.slice(0, cut), bass: tail };
}

/**
 * Spells a chord symbol for display: `"F#m7"` → `"Fa♯ m7"` / `"F♯m7"`.
 *
 * Only the root (and the slash bass) is translated — the quality suffix is
 * universal, so `maj7` stays `maj7` in both notations.
 */
export function chordSymbolLabel(
  symbol: string,
  notation: NoteNotation,
): string {
  if (notation === "english") {
    return formatAccidentals(symbol);
  }

  if (isAlreadyLatin(symbol)) {
    return symbol;
  }

  const { head, bass } = splitSlashBass(symbol);
  const parts = splitChordId(head);

  if (!parts) {
    return formatAccidentals(symbol);
  }

  const root = noteLabel(parts.root, "latin");
  const suffix = formatAccidentals(parts.suffix);
  const body = suffix ? `${root}${ROOT_SUFFIX_GAP}${suffix}` : root;

  if (!bass) {
    return body;
  }

  return `${body}/${noteLabel(bass, "latin")}`;
}
